import React, { useState, useCallback, useRef } from "react";
import { useThree, ThreeEvent } from "@react-three/fiber";
import * as THREE from "three";
import { observer } from "mobx-react-lite";
import { useFloorplannerStore } from '../../store/floorplannerStore';
import SingleLine from "./SingleLine"; // Import the new SingleLine component
import { SingleLineType, WallConnectionEnd, WallConnectionStart, zIndexEpsilon } from "../../types/floorplannerTypes";
import { editorStore } from "../../store/editorStore";
import { renderStore } from "../../store/renderStore";
import { updateDragHandles } from "./updateDragHandles";
import { projectToWorld } from "./projectToWorld";
import { action } from "mobx";
import { dragControlHandle } from "./dragControlHandle";
import { updateAlignmentLines } from "./updateAligmentLines";
import { dragWallHandle } from "./dragWallHandle";
import {
  endpointHandleHitSize,
  getMiddleHandleSizeForLength,
  getPreferredSingleLineMiddleHandleSize,
  handleSize,
  singleLineHandleZ,
} from "./createMiddleDragHandle";
import { dragStartHandle } from "./dragStartHandle";
import { dragEndHandle } from "./dragEndHandle";
import { updateWallConnections } from "./updateWallConnections";
import { dragMiddleVHandle } from "./dragMiddleVHandle";
import { dragMiddleHHandle } from "./dragMiddleHHandle";
import { MiddleVHandle } from "./MiddleVHandle";
import { MiddleHHandle } from "./MiddleHHandle";
import { useDragSelectedObjects } from "../../hooks/useDragSelectedObjects";
import { getQuadraticBezierPoint } from "../../utils/arcUtils";
import { CurveDragHandle } from "./CurveDragHandle";
import { CANVAS_ACCENT_COLOR, CANVAS_HOVER_COLOR, canvasHandleFillColor } from "../../constants/brandColors";

const SingleLinesStructure = observer(() => {
  const floorplannerStore = useFloorplannerStore();
  const { camera, gl } = useThree();
  const dragLine = useRef<SingleLineType | undefined>(undefined);
  const dragOffset = useRef<[number, number, number, number] | null>(null);
  const dragHandle = useRef<{ handle: string; startPosition: THREE.Vector3; endPosition: THREE.Vector3 }>({
    handle: "",
    startPosition: new THREE.Vector3(),
    endPosition: new THREE.Vector3(),
  });
  const [hoveredLine, setHoveredLine] = useState<string | null>(null);
  const [hoveredHandle, setHoveredHandle] = useState<string | null>(null);
  const isHovered = (handle: string) => hoveredHandle === handle;
  const [dragEndFreeToConnect, setDragEndFreeToConnect] = useState(false);
  const noopRaycast = () => null;
  const aboutToDrag = useRef(false);
  const beginLineDragCalled = useRef(false);
  const { startDraggingGroupSelection } = useDragSelectedObjects(gl, camera);
  const selectionHandlesVisible = editorStore.shouldShowSelectionHandles();

  const handlePointerMove = useCallback(
    action((e: PointerEvent) => {
      if (dragLine.current !== undefined && dragOffset.current) {
        if (aboutToDrag.current) {
          aboutToDrag.current = false;
          floorplannerStore.beginUndoTransaction('dragLine');
          floorplannerStore.recordLineForUndo(dragLine.current.id);
        }
        if (!editorStore.lineDragging) editorStore.setLineDragging(dragLine.current.id);
        let [newX, newY] = projectToWorld(e.clientX, e.clientY, gl, camera);
        const [startOffsetX, startOffsetY, endOffsetX, endOffsetY] =
          dragOffset.current;
        const line = dragLine.current;
        if (!line) return;

        // Middle handles should NOT call beginLineDrag as they don't affect connections
        const isMiddleHandle = dragHandle.current.handle === "middleV" || dragHandle.current.handle === "middleH";

        if (!beginLineDragCalled.current && !isMiddleHandle) {
          // Disconnect connections for wall, start, and end handle drags
          // Start/end handles disconnect only that specific end during drag
          const shouldDisconnectAll = dragHandle.current.handle === 'wall';
          const disconnectEnd = dragHandle.current.handle === 'start' ? 'start' : 
                               dragHandle.current.handle === 'end' ? 'end' : undefined;
          
          floorplannerStore.beginLineDrag(line.id, { 
            disconnectConnections: shouldDisconnectAll,
            disconnectEnd: disconnectEnd
          });
          beginLineDragCalled.current = true;
        }

        let delta = new THREE.Vector2(0, 0);
        let snapped = false;
        if (dragHandle.current.handle === "start") {
          snapped = dragStartHandle(line, startOffsetX, startOffsetY, newX, newY, dragEndFreeToConnect, delta);
        } else if (dragHandle.current.handle === "end") {
          snapped = dragEndHandle(line, endOffsetX, endOffsetY, newX, newY, dragEndFreeToConnect, delta);
        } else if (dragHandle.current.handle === "middleV") {
          snapped = dragMiddleVHandle(line, startOffsetX, startOffsetY, newX, newY, delta);
        } else if (dragHandle.current.handle === "middleH") {
          snapped = dragMiddleHHandle(line, startOffsetX, startOffsetY, newX, newY, delta);
        } else if (dragHandle.current.handle === "wall") {
          snapped = dragWallHandle(line, startOffsetX, startOffsetY, endOffsetX, endOffsetY, newX, newY, delta);
        } else if (dragHandle.current.handle === "control") {
          dragControlHandle(line, newX, newY);
        }
        const latestLine = floorplannerStore.findObjectId(line.id) as SingleLineType | undefined;
        if (latestLine) {
          dragLine.current = latestLine;
        }

        const handleLine = latestLine ?? line;
        updateDragHandles(dragHandle.current?.handle || "", handleLine, dragHandle);

        const alignmentPosition = (() => {
          if (!latestLine) {
            return { x: newX, y: newY };
          }
          if (dragHandle.current.handle === "start") {
            return { x: latestLine.start.x, y: latestLine.start.y };
          }
          if (dragHandle.current.handle === "end") {
            return { x: latestLine.end.x, y: latestLine.end.y };
          }
          return { x: newX, y: newY };
        })();

        updateAlignmentLines(
          line.id,
          alignmentPosition.x,
          alignmentPosition.y,
          dragHandle.current.handle,
        );

        if (!isMiddleHandle && !delta.equals(new THREE.Vector2(0, 0))) {
          updateWallConnections(
            line.id,
            dragHandle.current?.handle || null,
            [line.id],
            floorplannerStore
          );
        }
      }
    }),
    [editorStore.lineDragging, projectToWorld, updateDragHandles, camera.zoom],
  );

  const handlePointerUp = useCallback(() => {
    aboutToDrag.current = false;
    beginLineDragCalled.current = false;
    
    // IMPORTANT: Unblock dirty FIRST before setting dirty flag
    // This ensures that setDirty() calls will actually work
    floorplannerStore.setBlockDirty(false);
    
    if (dragLine.current) {
      const lineId = dragLine.current.id;
      
      // End the line drag and reconnect if close to other lines
      const affectedLineIds = floorplannerStore.endLineDrag(lineId);
      
      // Get the final line state AFTER ending drag to ensure we have the latest position
      const finalLine = floorplannerStore.findObjectId(lineId) as SingleLineType | undefined;
      
      // Force a position update to ensure the dirty flag is set with the final position
      // This is important because during drag, setDirty() was blocked
      if (finalLine) {
        // Update lastUpdate timestamp to ensure the change is recognized
        floorplannerStore.setObjectProperty(finalLine.id, 'lastUpdate', Date.now());
        // Force setDirty to ensure the position change is saved
        floorplannerStore.setDirty();
      }
      
      // Record changes for the dragged line and all affected lines (disconnected/reconnected)
      floorplannerStore.recordLineChange(lineId, 'modify');
      affectedLineIds.forEach(id => {
        if (id !== lineId) {
          // Check if it's a single line, wall, or ruler line and record accordingly
          const object = floorplannerStore.findObjectId(id);
          if (object) {
            if (object.type === 'singleLine') {
              floorplannerStore.recordLineChange(id, 'modify');
            } else if (object.type === 'wall') {
              floorplannerStore.recordWallChange(id, 'modify');
            } else if (object.type === 'rulerLine') {
              floorplannerStore.recordRulerChange(id, 'modify');
            }
          }
        }
      });
      
      // Push to undo stack (this also persists to localStorage)
      floorplannerStore.pushToUndoStack();
      
      // Ensure dirty flag is set one more time after all operations
      floorplannerStore.setDirty();
    }
    dragLine.current = undefined;
    dragOffset.current = null;
    dragHandle.current.handle = "";
    editorStore.setLineDragging(null);
    // Change cursor back to auto when dragging is done
    document.body.style.cursor = "auto";
    gl.domElement.removeEventListener("pointermove", handlePointerMove);
    gl.domElement.removeEventListener("pointerup", handlePointerUp);

    // Process deferred room updates after drag ends using requestIdleCallback
    // This reduces the work done in the main thread and improves performance
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        floorplannerStore.processRoomUpdates();
      });
    } else {
      // Fallback for browsers that don't support requestIdleCallback
      setTimeout(() => {
        floorplannerStore.processRoomUpdates();
      }, 0);
    }

    editorStore.setWhichEndToUpdate(undefined);
  }, [gl.domElement, handlePointerMove]);


  // Define the callback functions with appropriate types
  const onLinePointerDown = useCallback((event: ThreeEvent<PointerEvent>, id: string) => {
    if (editorStore.lineDragging || editorStore.wallConstructionMode || editorStore.lineConstructionMode || editorStore.rulerConstructionMode || editorStore.areaConstructionMode || editorStore.areaFillConstructionMode || editorStore.areaBleedFillMode) return;
    // View-only: select to inspect measurements, but don't start a drag.
    if (floorplannerStore.lockedByOther) {
      event.stopPropagation();
      const lockedLine = floorplannerStore.singleLinesMap.get(id);
      if (!lockedLine) return;
      if (!editorStore.isShiftPressed) {
        editorStore.clearSelections();
      }
      editorStore.addSelection(lockedLine);
      floorplannerStore.selectLine(lockedLine.id);
      return;
    }
    event.stopPropagation();
    aboutToDrag.current = true;
    const line = floorplannerStore.singleLinesMap.get(id);
    if (!line) return;
    if (editorStore.selections.length > 1 && line.selected) {
      startDraggingGroupSelection(event);
      return;
    }
    // Change cursor to grabbing when dragging a wall
    document.body.style.cursor = "grabbing";
    dragLine.current = line;
    updateDragHandles(dragHandle.current?.handle || "wall", line, dragHandle);
    const [worldX, worldY] = projectToWorld(event.clientX, event.clientY, gl, camera);
    const startOffsetX = worldX - line.start.x;
    const startOffsetY = worldY - line.start.y;
    const endOffsetX = worldX - line.end.x;
    const endOffsetY = worldY - line.end.y;
    dragOffset.current = [startOffsetX, startOffsetY, endOffsetX, endOffsetY];
    if (!editorStore.isShiftPressed && editorStore.selections.find((s) => s.id === line.id) === undefined) {
      editorStore.clearSelections();
    }
    editorStore.addSelection(line);
    floorplannerStore.selectLine(line.id);

    gl.domElement.addEventListener("pointermove", handlePointerMove);
    gl.domElement.addEventListener("pointerup", handlePointerUp);
    floorplannerStore.setBlockDirty(true);

  }, []);

  const onLinePointerEnter = useCallback((event: ThreeEvent<PointerEvent>, id: string) => {
    if (editorStore.lineDragging || editorStore.wallConstructionMode || editorStore.lineConstructionMode || editorStore.rulerConstructionMode || editorStore.areaConstructionMode || editorStore.areaFillConstructionMode || editorStore.areaBleedFillMode || renderStore.isDragging) return;
    event.stopPropagation();
    // Change cursor to grab when hovering over the line
    if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.add("cursor-vector");
    setHoveredLine(id);
  }, []);

  const onLinePointerLeave = useCallback((event: ThreeEvent<PointerEvent>, id: string) => {
    if (editorStore.lineDragging || editorStore.wallConstructionMode || editorStore.lineConstructionMode || editorStore.rulerConstructionMode || editorStore.areaConstructionMode || editorStore.areaFillConstructionMode || editorStore.areaBleedFillMode || renderStore.isDragging) return;
    // Reset cursor when leaving a wall
    if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.remove("cursor-vector");;
    setHoveredLine(null);
  }, []);

  const handleKeyPress = useCallback((event: React.KeyboardEvent<HTMLInputElement>, line: SingleLineType) => {
    // Implementation for key press handling
    if (event.key === "Enter") {
      // Implementation for Enter key press
    }
  }, []);

  const handleLengthClick = useCallback((lineId: string) => {
    // Implementation for handling length click
  }, []);

  const onLineSelectionPointerDown = useCallback(
    (
      event: ThreeEvent<PointerEvent>,
      line: SingleLineType,
      handle: "start" | "end" | "middleV" | "middleH" | "control",
    ) => {
      event.stopPropagation();
      aboutToDrag.current = true;
      dragLine.current = line;
      updateDragHandles(handle, line, dragHandle);
      const [worldX, worldY] = projectToWorld(event.clientX, event.clientY, gl, camera);
      let offsetX, offsetY;

      if (handle === "middleV" || handle === "middleH" || handle === "control") {
        const midpointX = (line.start.x + line.end.x) / 2;
        const midpointY = (line.start.y + line.end.y) / 2;
        offsetX = worldX - midpointX;
        offsetY = worldY - midpointY;
      } else {
        offsetX = worldX - (handle === "start" ? line.start.x : line.end.x);
        offsetY = worldY - (handle === "start" ? line.start.y : line.end.y);
      }
      if (handle === "start" || handle === "end") {
        const isWallEdgeConnected = line.centerConnections?.find(
          (c) => (c.targetPosition === WallConnectionStart || c.targetPosition === WallConnectionEnd) &&
            c.sourcePosition === (handle === 'start' ? WallConnectionStart : WallConnectionEnd),
        );
        setDragEndFreeToConnect(!isWallEdgeConnected);
      }

      dragOffset.current = [offsetX, offsetY, offsetX, offsetY];
      if (!editorStore.isShiftPressed && editorStore.selections.find((s) => s.id === line.id) === undefined) {
        editorStore.clearSelections();
      }
      editorStore.addSelection(line);
      floorplannerStore.selectLine(line.id);
      gl.domElement.addEventListener("pointermove", handlePointerMove);
      gl.domElement.addEventListener("pointerup", handlePointerUp);
      floorplannerStore.setBlockDirty(true);
    },
    [
      floorplannerStore.singleLinesMap,
      updateDragHandles,
      projectToWorld,
      gl.domElement,
      handlePointerMove,
      handlePointerUp,
      camera.zoom
    ],
  );

  return (
    <group>
      {floorplannerStore.singleLinesMap.size > 0 &&
        Array.from(floorplannerStore.singleLinesMap.values()).map((line) => {
          const isTranslating = renderStore.isObjectTranslating(line.id);
          const offset = isTranslating ? renderStore.groupTranslateOffset : { x: 0, y: 0 };

          // Calculate rotation offset for optimized group rotation
          const isRotating = renderStore.isObjectRotating(line.id);
          const rotationData = isRotating
            ? { angle: renderStore.groupRotationAngle, center: renderStore.groupRotationCenter }
            : null;

          // Calculate final position with both translate and rotation offsets
          let finalPosition = [offset.x, offset.y, 0] as [number, number, number];
          let groupRotation = [0, 0, 0] as [number, number, number];
          if (rotationData) {
            const { angle, center } = rotationData;
            const cos = Math.cos(angle);
            const sin = Math.sin(angle);
            const rotationCompX = center.x * (1 - cos) + center.y * sin;
            const rotationCompY = center.y * (1 - cos) - center.x * sin;
            finalPosition = [offset.x + rotationCompX, offset.y + rotationCompY, 0];
            groupRotation = [0, 0, angle];
          }
          const lineLength = line.start.distanceTo(line.end);
          const middleHandleSize = getMiddleHandleSizeForLength(
            lineLength,
            getPreferredSingleLineMiddleHandleSize(),
          );
          const endpointHitSize = endpointHandleHitSize();
          const handleZ = singleLineHandleZ + ((line.zIndex || 0) * zIndexEpsilon);
          const endpointHandleZ = handleZ + 0.01;
          const middleHandleCenter = line.lineForm === "arc" && line.controlPoint
            ? getQuadraticBezierPoint(0.5, line.start, line.controlPoint, line.end)
            : line.start.clone().add(line.end).multiplyScalar(0.5);
          const middleHandleRotation = new THREE.Vector2().subVectors(line.end, line.start).angle();

          return (
            <group
              key={`group-line-${line.id}`}
              position={finalPosition}
              rotation={groupRotation}
            >
              <SingleLine
                key={`line-${line.id}`}
                line={line}
                color={line.selected
                  ? CANVAS_ACCENT_COLOR
                  : hoveredLine === line.id
                    ? (line.hoverColor?.toString() ?? CANVAS_HOVER_COLOR)
                    : line.lineColor
                      ? line.lineColor.toString()
                      : "black"}
                onLinePointerDown={onLinePointerDown}
                onLinePointerEnter={onLinePointerEnter}
                onLinePointerLeave={onLinePointerLeave}
                handleKeyPress={handleKeyPress}
                handleLengthClick={handleLengthClick}
              />
              {(selectionHandlesVisible && line.selected && !editorStore.wallConstructionMode && !editorStore.lineConstructionMode && !editorStore.rulerConstructionMode && !editorStore.areaConstructionMode && !editorStore.areaFillConstructionMode && !editorStore.areaBleedFillMode) && (
                <group>
                  {(editorStore.lineDragging !== line.id || dragHandle.current?.handle === "start") && (
                    <group
                      position={new THREE.Vector3(line.start.x, line.start.y, endpointHandleZ)}
                      onPointerDown={(event) => onLineSelectionPointerDown(event, line, "start")}
                      onPointerEnter={(event) => {
                        event.stopPropagation();
                        document.body.style.cursor = "move";
                        setHoveredHandle("start")
                      }}
                      onPointerLeave={(event) => {
                        event.stopPropagation();
                        if (editorStore.lineDragging !== line.id) document.body.style.cursor = "auto";
                        setHoveredHandle(null)
                      }}
                    >
                      <mesh>
                        <planeGeometry args={[endpointHitSize, endpointHitSize]} />
                        <meshBasicMaterial
                          //color="white"
                          transparent
                          opacity={0.0}
                        />
                      </mesh>
                      <mesh raycast={noopRaycast}>
                        <torusGeometry args={[handleSize / editorStore.zoomLevelDivisor() / 1.8, 0.003, 16, 100]} />
                        <meshBasicMaterial color={CANVAS_ACCENT_COLOR} />
                      </mesh>
                      <mesh
                        position={[0, 0, 0]}
                        rotation={[Math.PI / 2, 0, 0]}
                        raycast={noopRaycast}
                      >
                        <cylinderGeometry args={[handleSize / editorStore.zoomLevelDivisor() / 1.8, 0, 0, 32]} />
                        <meshBasicMaterial color={isHovered("start") ? CANVAS_HOVER_COLOR : "white"} />
                      </mesh>
                    </group>
                  )}
                  {(editorStore.lineDragging !== line.id || dragHandle.current?.handle === "end") && (
                    <group
                      position={new THREE.Vector3(line.end.x, line.end.y, endpointHandleZ)}
                      onPointerDown={(event) => onLineSelectionPointerDown(event, line, "end")}
                      onPointerEnter={(event) => {
                        event.stopPropagation();
                        document.body.style.cursor = "move";
                        setHoveredHandle("end")
                      }}
                      onPointerLeave={(event) => {
                        event.stopPropagation();
                        if (editorStore.lineDragging !== line.id) document.body.style.cursor = "auto";
                        setHoveredHandle(null)
                      }}
                    >
                      <mesh>
                        <planeGeometry args={[endpointHitSize, endpointHitSize]} />
                        <meshBasicMaterial
                          //color="white"
                          transparent
                          opacity={0.0}
                        />
                      </mesh>
                      <mesh raycast={noopRaycast}>
                        <torusGeometry args={[handleSize / editorStore.zoomLevelDivisor() / 1.8, 0.003, 16, 100]} />
                        <meshBasicMaterial color={CANVAS_ACCENT_COLOR} />
                      </mesh>
                      <mesh
                        position={[0, 0, 0]}
                        rotation={[Math.PI / 2, 0, 0]}
                        raycast={noopRaycast}
                      >
                        <cylinderGeometry args={[handleSize / editorStore.zoomLevelDivisor() / 1.8, 0, 0, 32]} />
                        <meshBasicMaterial color={isHovered("end") ? CANVAS_HOVER_COLOR : "white"} />
                      </mesh>
                    </group>
                  )}
                  {/* Middle handles */}
                  {(editorStore.lineDragging !== line.id || dragHandle.current?.handle === "middleV") && (
                    <MiddleVHandle
                      wall={line}
                      wallWidth={middleHandleSize}
                      centerPoint={middleHandleCenter}
                      onHandlePointerDown={(event: ThreeEvent<PointerEvent>) => onLineSelectionPointerDown(event, line, "middleV")}
                      onHandlePointerEnter={(event: ThreeEvent<PointerEvent>) => {
                        event.stopPropagation();
                        document.body.classList.add("cursor-vector")
                        setHoveredHandle("middleV")
                      }}
                      onHandlePointerLeave={(event: ThreeEvent<PointerEvent>) => {
                        event.stopPropagation();
                        if (editorStore.lineDragging !== line.id) document.body.classList.remove("cursor-vector")
                        setHoveredHandle(null)
                      }}
                      fillColor={canvasHandleFillColor(dragHandle.current?.handle === "middleV", isHovered("middleV"))}
                      color={isHovered("middleV") || dragHandle.current?.handle === "middleV" ? "white" : "black"}
                    />
                  )}
                  {(editorStore.lineDragging !== line.id || dragHandle.current?.handle === "middleH") && (
                    <MiddleHHandle
                      wall={line}
                      wallWidth={middleHandleSize}
                      centerPoint={middleHandleCenter}
                      onHandlePointerDown={(event: ThreeEvent<PointerEvent>) => onLineSelectionPointerDown(event, line, "middleH")}
                      onHandlePointerEnter={(event: ThreeEvent<PointerEvent>) => {
                        event.stopPropagation();
                        document.body.classList.add("cursor-vector")
                        setHoveredHandle("middleH")
                      }}
                      onHandlePointerLeave={(event: ThreeEvent<PointerEvent>) => {
                        event.stopPropagation();
                        if (editorStore.lineDragging !== line.id) document.body.classList.remove("cursor-vector")
                        setHoveredHandle(null)
                      }}
                      fillColor={canvasHandleFillColor(dragHandle.current?.handle === "middleH", isHovered("middleH"))}
                      color={isHovered("middleH") || dragHandle.current?.handle === "middleH" ? "white" : "black"}
                    />
                  )}
                  {(editorStore.lineDragging !== line.id || dragHandle.current?.handle === "control") && (
                    <group
                      position={new THREE.Vector3(middleHandleCenter.x, middleHandleCenter.y, handleZ)}
                      rotation={[0, 0, middleHandleRotation]}
                      onPointerDown={(event) => onLineSelectionPointerDown(event, line, "control")}
                      onPointerEnter={(event) => {
                        event.stopPropagation();
                        document.body.style.cursor = "move";
                        setHoveredHandle("control")
                      }}
                      onPointerLeave={(event) => {
                        event.stopPropagation();
                        if (editorStore.lineDragging !== line.id) document.body.style.cursor = "auto";
                        setHoveredHandle(null)
                      }}
                    >
                      <mesh raycast={noopRaycast}>
                        <planeGeometry args={[middleHandleSize * 1.4, middleHandleSize * 1.4]} />
                        <meshBasicMaterial transparent opacity={0.0} />
                      </mesh>
                      <CurveDragHandle
                        object={line}
                        size={middleHandleSize}
                        fillColor={canvasHandleFillColor(dragHandle.current?.handle === "control", isHovered("control"))}
                        color={isHovered("control") || dragHandle.current?.handle === "control" ? "white" : "black"}
                      />
                    </group>
                  )}

                </group>
              )}
            </group>
          );
        })
      }
    </group>
  );
});

export default SingleLinesStructure;
